/* start - flights_split_bounds_us */
import { List } from "@bookingcom/bui-react";
import { UIFlightData } from "@flights/types";
import React, { useEffect, useRef } from "react";
import FlightCardBound from "../FlightCard/components/FlightCardBound.desktop";
import { isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import Frame from "../Frame";
import { FlightCampaignTCBanner } from "../FlightCampaignTCBanner";
import { RemoteAppleCardSR } from "../../remote";
import GeniusTCBanner from "../../../cross-sell/components/GeniusTCBanner";
import { SearchTrafficAAGeniusTrackerDesktop } from "./SearchResults.desktop";
import Wrapper from "../../atoms/Wrapper";
import EsimBanner from "../../../ancillaries/components/Esim/EsimBanner";
import useSearchResultsListDesktopController from "./useSearchResultsList.desktop.controller";
import useIsInViewport from "../../../hooks/useIsInViewport";
import { useEsimSearch } from "../../../ancillaries/hooks/Esim/useEsimFetch";
import { getEsimEligibilitySearch } from "../../../ancillaries/store/esim/selectors";
import { useSelector } from "react-redux";
import flights_ace_web_esim_banner from "../../../utils/experiments/ace/flights_ace_web_esim_banner";
import FlightCardWrapper from "./FlightCardWrapper";

type Props = {
  flights: UIFlightData[];
  onSelectFlight: (flight: UIFlightData) => void;
};

const SearchResultsListOutboundDesktop = ({ flights, onSelectFlight }: Props) => {
  const { campaignBanner, remoteBanners, searchType } = useSearchResultsListDesktopController();

  /* start - flights_ace_web_esim_banner */
  const isEsimEligible = !!useSelector(getEsimEligibilitySearch);
  const esimBannerRef = useRef<HTMLDivElement>(null);
  const eSimInViewport = useIsInViewport(esimBannerRef.current);
  const fetchEsim = useEsimSearch();

  const showEsimBanner = isEsimEligible && !!flights_ace_web_esim_banner.trackWithDefaultStage();
  const ESIM_BANNER_SEARCH_RESULTS_INDEX = 6;

  useEffect(() => {
    void fetchEsim();
  }, [fetchEsim]);

  useEffect(() => {
    if (eSimInViewport) {
      flights_ace_web_esim_banner.stages.viewed_esim_banner_sr_page();
      flights_ace_web_esim_banner.goals.viewed_esim_banner();
    }
  }, [eSimInViewport]);
  /* end - flights_ace_web_esim_banner */

  return (
    <List attributes={{ style: { padding: 0 } }}>
      {flights.map((flight, index) => {
        return (
          <div key={`${index}_${flight.token}`}>
            {isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_WHITE_LABEL_TC_BANNER_IN_FE") &&
            campaignBanner &&
            index === campaignBanner.position ? (
              <Frame mt={2} mb={4}>
                <FlightCampaignTCBanner campaignBanner={campaignBanner} />
              </Frame>
            ) : null}

            {Array.isArray(remoteBanners) &&
            remoteBanners.includes("APPLE_CARD_BANNER_SR") &&
            index === flights.length - 1 ? (
              <RemoteAppleCardSR />
            ) : undefined}

            <FlightCardWrapper index={index}>
              <FlightCardBound
                flight={flight}
                onClick={onSelectFlight}
                pricePerPerson
                index={index}
                searchType={searchType}
                boundsVariant="outbound"
              />
            </FlightCardWrapper>

            {/* If GeniusCampaign is Enabled, show the Genius TC campaign on 3rd position */}
            {!isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_WHITE_LABEL_TC_BANNER_IN_FE") &&
            index === 1 &&
            campaignBanner ? (
              <Frame mt={2} mb={4}>
                <GeniusTCBanner campaignBanner={campaignBanner} />
                <SearchTrafficAAGeniusTrackerDesktop />
              </Frame>
            ) : null}

            {/**
             * If the position is larger than the number of cards displayed in the SR page,
             * then the T&C banner is shown after the last card.
             * More info: https://jira.booking.com/jira/browse/FPRICING-809
             */}
            {isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_WHITE_LABEL_TC_BANNER_IN_FE") &&
            campaignBanner &&
            campaignBanner.position >= flights.length && // campaign banner position is larger than the number of cards
            index >= flights.length - 1 ? ( // after the last flight card
              <Frame mt={2} mb={4}>
                <FlightCampaignTCBanner campaignBanner={campaignBanner} />
              </Frame>
            ) : null}

            {/* start - flights_ace_web_esim_banner */}
            {showEsimBanner && index === ESIM_BANNER_SEARCH_RESULTS_INDEX ? (
              <Wrapper
                elementRef={esimBannerRef}
                noBorder
                noPadding
                noPaddingVertical
                style={{
                  paddingBottom: "8px",
                  paddingBlockStart: "0px"
                }}
              >
                <EsimBanner context="search" />
              </Wrapper>
            ) : null}
            {/* end - flights_ace_web_esim_banner */}
          </div>
        );
      })}{" "}
    </List>
  );
};

export default React.memo(SearchResultsListOutboundDesktop);
/* end - flights_split_bounds_us */
